/**
 * The CLIENT-SIDE half of the admin gate.
 *
 * ⛔⛔ THIS IS NOT THE ACCESS CONTROL AND MUST NEVER BE DESCRIBED AS ONE. Every admin endpoint carries
 *   the API's own `requireAdmin` preHandler, independently, and would refuse this same user with a
 *   403 if they reached it another way — by typing the URL, by an old bookmark, or with curl. Hiding
 *   a button is not authorisation; anyone can open the console and call the endpoint.
 *
 * ⭐ So why have it at all? Because the alternative is a nav entry that leads every non-admin to a
 *   screen of red boxes. Both halves exist ON PURPOSE and NEITHER is sufficient alone: a UI check
 *   with no server check is theatre, and a server check with no UI check is a broken-looking product.
 *
 * ⚠️ It reads `is_admin` off the SESSION (`/auth/me`), which is the server's answer rather than
 *   anything this app decided. A local `isAdmin` boolean kept in client state would drift the moment
 *   an admin revoked the grant, and would drift in the permissive direction.
 */
import type { ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { EmptyState } from '../../components/ui/EmptyState';

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { state } = useAuth();

  // ⭐ `RequireAuth` already handled `checking`, `unknown` and `anonymous` further up the tree, so
  //   the only question left here is the flag itself.
  if (state.status !== 'authenticated') return null;

  if (!state.user.is_admin) {
    return (
      <EmptyState
        title="Administrators only"
        detail="This screen manages user accounts and projects. Your account does not have the administrator flag — ask someone who does."
      />
    );
  }

  return <>{children}</>;
}
